/**
 * CharacterSwitcher — dropdown del header que:
 *  - Lista los chars linkeados del dashboard_user actual
 *  - Switcha el active char (POST /auth/switch/:id → reload)
 *  - Linkea un char nuevo (window.location.assign(/auth/login))
 *  - Sale de la cuenta (POST /auth/logout)
 *
 * Patrón UX: hard-reload al switchear coincide con el comportamiento
 * existente de "Salir" — kills the QueryClient, rehidrata desde cero.
 * Evita tener que refactorear 30+ query keys para multi-char (MVP).
 *
 * a11y: misma chrome que NavDropdown (click-outside, Escape, focus-return,
 * roving tabindex, ARIA menu).
 */

import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import type { DashboardCharacter } from '../api'

/** Shape que viene de useAuth() / api.me(). Exportado para callers de App. */
export interface MeData {
  authenticated: boolean
  character?: {
    id: number
    name: string
    portrait: string | null
    corporation_id: number
    alliance_id: number | null
  }
  dashboard_user_id?: string
  active_character_id?: number
  characters?: DashboardCharacter[]
  roles_bitmask?: number
}

export function CharacterSwitcher({ me }: { me: MeData | undefined }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [focusedIdx, setFocusedIdx] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  const active = me?.character
  const characters = me?.characters ?? []
  // Items del menú: [chars..., "Link another character", "Salir"].
  // Calculamos el total real para el roving tabindex.
  const itemCount = characters.length + 2

  // Reset focus al abrir; limpiar al cerrar.
  useEffect(() => {
    if (open) setFocusedIdx(0)
    else setFocusedIdx(-1)
  }, [open])

  // Mover el foco cuando cambia el focusedIdx.
  useEffect(() => {
    if (open && focusedIdx >= 0) itemRefs.current[focusedIdx]?.focus()
  }, [open, focusedIdx])

  // Click outside → close.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  // Escape → close + return focus al trigger.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Si el active char deja de existir (logout de otro browser), redirigir.
  useEffect(() => {
    if (me && me.authenticated === false && typeof location !== 'undefined') {
      location.assign('/login')
    }
  }, [me])

  if (!active) return null

  const isMulti = characters.length > 1

  const onSwitch = async (id: number) => {
    if (id === me?.active_character_id) {
      setOpen(false)
      return
    }
    setBusy(true)
    try {
      await api.switchCharacter(id)
      // Hard reload para rehidratar TODO el QueryClient con el nuevo char.
      // Coincide con el patrón del "Salir" actual (logout → reload).
      window.location.reload()
    } catch (err) {
      // 401 ya está manejado por http(); otros errores los mostramos
      // silenciosamente y reabrimos el menú para retry.
      console.error('[CharacterSwitcher] switch failed:', err)
      setBusy(false)
      setOpen(false)
      // Si fue un error grave, refrescar /me para ver el estado real.
      qc.invalidateQueries({ queryKey: ['me'] })
    }
  }

  const onLinkAnother = () => {
    // Disparamos el login flow → callback detecta el cookie activo y
    // linkea el nuevo char al mismo dashboard_user.
    setOpen(false)
    window.location.assign(api.loginUrl())
  }

  const onLogout = async () => {
    setBusy(true)
    try {
      await api.logout()
    } finally {
      window.location.assign('/login')
    }
  }

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === 'ArrowDown' ||
      e.key === 'Enter' ||
      e.key === ' ' ||
      e.key === 'ArrowUp'
    ) {
      e.preventDefault()
      setOpen(true)
    }
  }

  const onItemKeyDown = (e: React.KeyboardEvent, i: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIdx((i + 1) % itemCount)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIdx((i - 1 + itemCount) % itemCount)
    } else if (e.key === 'Home') {
      e.preventDefault()
      setFocusedIdx(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      setFocusedIdx(itemCount - 1)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      buttonRef.current?.focus()
    } else if (e.key === 'Tab') {
      setOpen(false)
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Switch character. Current: ${active.name}`}
        disabled={busy}
        className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-eve-panel/60 transition-colors disabled:opacity-50"
      >
        {active.portrait ? (
          <img
            src={active.portrait}
            alt=""
            className="w-8 h-8 rounded-full border border-eve-accent/40"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-eve-panel border border-eve-border" />
        )}
        <span className="text-sm font-medium hidden sm:inline text-eve-text max-w-[140px] truncate">
          {active.name}
        </span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          className={`transition-transform text-eve-muted ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <path d="M2 4 L5 7 L8 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Character switcher"
          className="absolute top-full right-0 mt-2 min-w-[260px] bg-eve-panel border border-eve-border rounded-md shadow-lg py-1 z-20"
        >
          {isMulti && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.1em] text-eve-muted">
                Tus personajes
              </div>
              {characters.map((c, i) => {
                const isActive = c.id === me?.active_character_id
                return (
                  <button
                    key={c.id}
                    ref={(el) => {
                      itemRefs.current[i] = el
                    }}
                    type="button"
                    role="menuitem"
                    tabIndex={focusedIdx === i ? 0 : -1}
                    onClick={() => onSwitch(c.id)}
                    onKeyDown={(e) => onItemKeyDown(e, i)}
                    className={`flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-eve-bg/60 transition-colors ${
                      isActive ? 'text-eve-accent' : 'text-eve-text'
                    }`}
                  >
                    {c.portrait ? (
                      <img
                        src={c.portrait}
                        alt=""
                        className="w-7 h-7 rounded-full border border-eve-border shrink-0"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-eve-bg border border-eve-border shrink-0" />
                    )}
                    <span className="flex-1 truncate text-sm">{c.name}</span>
                    {isActive && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        aria-hidden="true"
                        className="shrink-0 text-eve-accent"
                      >
                        <path d="M2 7 L6 11 L12 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                )
              })}
              <div className="my-1 border-t border-eve-border" />
            </>
          )}

          {/* "Link another character" — siempre visible si el menu está abierto. */}
          <button
            ref={(el) => {
              itemRefs.current[characters.length] = el
            }}
            type="button"
            role="menuitem"
            tabIndex={focusedIdx === characters.length ? 0 : -1}
            onClick={onLinkAnother}
            onKeyDown={(e) => onItemKeyDown(e, characters.length)}
            disabled={busy}
            className="flex items-center gap-3 w-full px-3 py-2 text-left text-eve-text hover:bg-eve-bg/60 transition-colors disabled:opacity-50"
          >
            <div className="w-7 h-7 rounded-full border border-dashed border-eve-border flex items-center justify-center text-eve-muted shrink-0">
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <path d="M6 2 L6 10 M2 6 L10 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <span className="flex-1 text-sm italic">Link another character</span>
          </button>

          <div className="my-1 border-t border-eve-border" />

          <button
            ref={(el) => {
              itemRefs.current[characters.length + 1] = el
            }}
            type="button"
            role="menuitem"
            tabIndex={focusedIdx === characters.length + 1 ? 0 : -1}
            onClick={onLogout}
            onKeyDown={(e) => onItemKeyDown(e, characters.length + 1)}
            disabled={busy}
            className="block w-full text-left px-3 py-2 text-xs text-eve-muted hover:text-eve-accent transition-colors uppercase tracking-wider disabled:opacity-50"
          >
            Salir
          </button>
        </div>
      )}
    </div>
  )
}
